import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { authApi } from '../../features/auth/authApi';
import { isApiError } from '../../lib/apiError';
import { Alert } from '../ui/Alert';
import { BottomSheet } from '../ui/BottomSheet';
import { Button } from '../ui/Button';
import { TextField } from '../ui/TextField';

/*
 * La misma politica que aplica el servidor (`passwordPolicy.ts`), repetida
 * aqui a proposito.
 *
 * No es duplicacion por descuido: el servidor sigue siendo quien decide, y
 * esto solo evita el viaje de ida y vuelta para descubrir que faltaba una
 * mayuscula. Si alguna vez discrepan, manda el servidor y su mensaje es el que
 * se ensena.
 */
const schema = z
  .object({
    current: z.string().min(1, 'Escribe tu contraseña actual.'),
    next: z
      .string()
      .min(12, 'La contraseña nueva debe tener al menos 12 caracteres.')
      .refine((value) => /[a-z]/.test(value), 'Debe incluir una minúscula.')
      .refine((value) => /[A-Z]/.test(value), 'Debe incluir una mayúscula.')
      .refine((value) => /\d/.test(value), 'Debe incluir un número.'),
    repeat: z.string().min(1, 'Repite la contraseña nueva.'),
  })
  .refine((value) => value.next !== value.current, {
    path: ['next'],
    message: 'La nueva debe ser distinta de la actual.',
  })
  .refine((value) => value.next === value.repeat, {
    path: ['repeat'],
    message: 'Las dos no coinciden.',
  });

interface ChangePasswordSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Cambio de la propia contraseña.
 *
 * Se pide la actual además de la nueva, y eso lo exige el servidor: sin ella,
 * un teléfono desbloqueado y olvidado un minuto encima de una mesa basta para
 * quedarse con la cuenta.
 *
 * Va en un panel inferior porque su primer usuario es el chofer, que trabaja
 * de pie y con una mano. Sirve igual en el escritorio.
 *
 * Se escribe dos veces la nueva. Aquí no hay «he olvidado mi contraseña» que
 * valga: si se teclea mal y se guarda, quien la cambió se queda fuera y hace
 * falta que un administrador se la reponga.
 */
export function ChangePasswordSheet({ isOpen, onClose }: ChangePasswordSheetProps): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [isDone, setIsDone] = useState(false);

  function reset(): void {
    setCurrent('');
    setNext('');
    setRepeat('');
    setIssues({});
    setIsDone(false);
  }

  const mutation = useMutation({
    mutationFn: (values: { current: string; next: string }) =>
      authApi.changePassword(values.current, values.next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setRepeat('');
      setIssues({});
      setIsDone(true);
    },
  });

  function submit(): void {
    const parsed = schema.safeParse({ current, next, repeat });

    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && !(field in found)) {
          found[field] = issue.message;
        }
      }
      setIssues(found);
      return;
    }

    setIssues({});
    mutation.mutate({ current, next });
  }

  return (
    <BottomSheet
      title="Cambiar contraseña"
      isOpen={isOpen}
      onClose={() => {
        reset();
        mutation.reset();
        onClose();
      }}
    >
      {isDone ? (
        <>
          <Alert tone="success" title="Contraseña cambiada">
            La próxima vez que entres, usa la nueva.
          </Alert>
          <Button
            fullWidth
            onClick={() => {
              reset();
              mutation.reset();
              onClose();
            }}
          >
            Listo
          </Button>
        </>
      ) : (
        <>
          <TextField
            label="Contraseña actual"
            type="password"
            autoComplete="current-password"
            value={current}
            error={issues.current}
            onChange={(event) => {
              setCurrent(event.target.value);
            }}
          />
          <TextField
            label="Contraseña nueva"
            type="password"
            autoComplete="new-password"
            hint="Mínimo 12 caracteres, con mayúscula, minúscula y número."
            value={next}
            error={issues.next}
            onChange={(event) => {
              setNext(event.target.value);
            }}
          />
          <TextField
            label="Repite la nueva"
            type="password"
            autoComplete="new-password"
            value={repeat}
            error={issues.repeat}
            onChange={(event) => {
              setRepeat(event.target.value);
            }}
          />

          {mutation.isError ? (
            <Alert tone="danger" title="No se pudo cambiar">
              {isApiError(mutation.error)
                ? mutation.error.message
                : 'Error inesperado. Inténtalo de nuevo.'}
            </Alert>
          ) : null}

          <Button fullWidth isLoading={mutation.isPending} onClick={submit}>
            Guardar la contraseña
          </Button>
        </>
      )}
    </BottomSheet>
  );
}
